import { resolveOperator } from './operator';

/**
 * Détail lisible d'une transaction à partir de la réponse du transaction-service (`raw`)
 * et des informations du paiement POS (`raw.pos`, via /pos/transaction-requests/references).
 */

const PROVIDER_TYPE_LABELS = {
    mobile_money: 'Mobile Money',
    bank: 'Virement bancaire',
    credit_card: 'Carte bancaire',
    cash: 'Espèces',
};

const TARGET_LABELS = {
    payment_request: 'Paiement au terminal',
    payment_link: 'Lien de paiement',
    invoice: 'Facture',
};

const STATUS_LABELS = {
    completed: 'Réussie',
    approved: 'Réussie',
    paid: 'Réussie',
    cashed: 'Réussie',
    pending: 'En attente',
    requested: 'En attente',
    failed: 'Échouée',
    canceled: 'Annulée',
    cancelled: 'Annulée',
    expired: 'Expirée',
};

export const transactionStatusLabel = (status) =>
    STATUS_LABELS[String(status || '').toLowerCase()] || 'Inconnu';

export const transactionStatusTone = (status) => {
    const s = String(status || '').toLowerCase();
    if (['completed', 'approved', 'paid', 'cashed'].includes(s)) return 'success';
    if (['pending', 'requested'].includes(s)) return 'pending';
    return 'failed';
};

const toDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
};

/** Masque le milieu d'un numéro pour le reçu client : +243 81••••567. */
export const maskPhone = (phone) => {
    const s = String(phone || '').trim();
    const digits = s.replace(/\D/g, '');
    if (digits.length < 7) return s;
    // On garde l'indicatif et le début, on masque 4 chiffres, on garde les 3 derniers
    const total = digits.length;
    let seen = 0;
    return s.replace(/\d/g, (d) => {
        seen += 1;
        return seen <= total - 7 || seen > total - 3 ? d : '•';
    });
};

/**
 * @param {Object} transaction - ligne telle qu'affichée par la page (reference, status, amount…)
 * @returns {Object} champs prêts à afficher ; `null` quand l'information n'existe pas
 */
export const buildTransactionDetail = (transaction = {}) => {
    const raw = transaction.raw || {};
    const pos = raw.pos || {};
    const status = raw.status || transaction.status;
    const requestedAt = toDate(raw.state?.requested_at) || toDate(raw.createdAt) || toDate(raw._date);
    const completedAt = toDate(raw.state?.completed_at) || toDate(pos.paid_at);

    const payerPhone = pos.payer_phone || null;
    const providerName = resolveOperator({ name: raw.provider_name || pos.provider_name, providerId: raw.provider_id, phone: payerPhone });
    const providerType = PROVIDER_TYPE_LABELS[String(raw.provider_type || '').toLowerCase()] || null;
    const isWithdrawal = transaction.type === 'withdrawal'
        || /^TXN-\d{8}-WDL/i.test(String(raw.code || ''))
        || /withdraw|retrait/i.test(String(raw.operation || ''));
    const payerName = raw.client_name || pos.payer_name || null;

    return {
        reference: transaction.reference || raw.code || raw.publicRef || raw.ref || raw.id,
        receiptId: raw.receiptId || transaction.receiptId || null,
        operatorRef: raw._id || null,
        status,
        statusLabel: transactionStatusLabel(status),
        statusTone: transactionStatusTone(status),
        amount: Number(raw.amount ?? transaction.amount ?? 0),
        fees: Number(raw.fees ?? transaction.fees ?? 0),
        netAmount: Number(raw.netAmount ?? transaction.netAmount ?? 0),
        currency: raw.currency || transaction.currency || 'CDF',
        label: raw.label || transaction.description || null,
        channel: TARGET_LABELS[pos.target] || transaction.typeLabel || null,
        method: providerType || (providerName ? 'Mobile Money' : null),
        provider: providerName,
        payerName: payerName || payerPhone || null,
        payerPhone,
        terminal: pos.terminal_tag || null,
        cashier: transaction.cashierName && transaction.cashierName !== '—' ? transaction.cashierName : null,
        caisse: transaction.caisseName || null,
        requestedAt,
        completedAt,
        direction: raw.direction || transaction.direction || null,
        isWithdrawal,
    };
};

export const formatMoney = (amount, currency = 'CDF') =>
    `${Number(amount || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${currency}`;

export const formatDateTime = (date) =>
    date ? date.toLocaleString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
